import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import {
  AGGREGATION_LABEL,
  currentQuarter,
  fetchDirectory,
  fetchLinkedTasks,
  fetchMyOkr,
  fetchMyOkrList,
  fetchOkrMetrics,
  isPastQuarter,
  quarterLabel,
  quarterOptions,
  reviewOkr,
  saveMyOkr,
  submitMyOkr,
  updateTaskStatus,
  type DirectoryUser,
  type KeyResultInput,
  type MetricAggregation,
  type OkrDirection,
  type OkrMetric,
  type OkrOwner,
  type OkrTaskStatus,
  type TaskMonth,
} from '../../api/okrs';
import { fieldToTarget, targetToField, type MetricUnit } from '../../components/formatters';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { DataState } from '../../components/DataState';
import { useAuth } from '../../auth/useAuth';
import {
  KeyResultProgress,
  LinkedUserChip,
  OkrStatusChip,
  STATUS_META,
  TaskListHeader,
  TaskRow,
  TaskStatusChip,
  TaskStatusSelect,
  taskDueLabel,
} from './OkrShared';

const KEY_SEP = '';
const krKey = (m: { layer: string; area: string; subarea: string; metric: string }) =>
  [m.layer, m.area, m.subarea, m.metric].join(KEY_SEP);

function metricLabel(m: { metric: string; subarea: string }): string {
  return m.subarea && m.subarea.trim() ? `${m.metric} · ${m.subarea}` : m.metric;
}

interface EditTask {
  id?: string;
  name: string;
  description: string;
  notionUrl: string;
  monthDue: TaskMonth;
  linkedUserId: string | null;
  linkedUser: OkrOwner | null;
}

interface EditRow {
  key: string;
  layer: string;
  area: string;
  subarea: string;
  metric: string;
  label: string;
  unit: MetricUnit;
  format: string;
  aggregation: MetricAggregation;
  direction: OkrDirection;
  target: string;
  tasks: EditTask[];
}

function rowFromMetric(m: OkrMetric): EditRow {
  return {
    key: m.key,
    layer: m.layer,
    area: m.area,
    subarea: m.subarea,
    metric: m.metric,
    label: metricLabel(m),
    unit: m.unit,
    format: m.format,
    aggregation: m.aggregation,
    direction: 'up',
    target: '',
    tasks: [],
  };
}

function rowsToInput(rows: EditRow[]): KeyResultInput[] {
  return rows.map((r) => ({
    layer: r.layer,
    area: r.area,
    subarea: r.subarea,
    metric: r.metric,
    targetValue: fieldToTarget(r.target, r.unit),
    direction: r.direction,
    tasks: r.tasks.map((t) => ({
      id: t.id,
      name: t.name.trim(),
      description: t.description.trim(),
      notionUrl: t.notionUrl.trim(),
      monthDue: t.monthDue,
      linkedUserId: t.linkedUserId,
    })),
  }));
}

function rowError(r: EditRow): string | null {
  if (r.target.trim() === '') return 'Target required';
  const n = Number(r.target);
  if (!Number.isFinite(n)) return 'Not a number';
  if (r.direction === 'down' ? n < 0 : n <= 0) return r.direction === 'down' ? 'Must be ≥ 0' : 'Must be > 0';
  return null;
}

function rowHasTaskError(r: EditRow): boolean {
  return r.tasks.some((t) => t.name.trim() === '');
}

export function MyOkrPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [quarter, setQuarter] = useState(currentQuarter());
  const [rows, setRows] = useState<EditRow[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const metricsQuery = useQuery({
    queryKey: ['okr-metrics'],
    queryFn: ({ signal }) => fetchOkrMetrics(signal).then((r) => r.metrics),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const listQuery = useQuery({
    queryKey: ['my-okr-list'],
    queryFn: ({ signal }) => fetchMyOkrList(signal).then((r) => r.okrs),
    refetchOnWindowFocus: false,
  });

  const okrQuery = useQuery({
    queryKey: ['my-okr', quarter],
    queryFn: ({ signal }) => fetchMyOkr(quarter, signal),
    refetchOnWindowFocus: false,
  });

  const directoryQuery = useQuery({
    queryKey: ['okr-directory'],
    queryFn: ({ signal }) => fetchDirectory(signal).then((r) => r.users),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const linkedQuery = useQuery({
    queryKey: ['linked-tasks', quarter],
    queryFn: ({ signal }) => fetchLinkedTasks(quarter, signal).then((r) => r.tasks),
    refetchOnWindowFocus: false,
  });

  const okr = okrQuery.data?.okr ?? null;
  const past = isPastQuarter(quarter);
  const editable = !past && (!okr || okr.status === 'draft' || okr.status === 'changes_requested');

  // Reset the editor from server state when the quarter or persisted plan changes.
  useEffect(() => {
    setRows(
      (okr?.keyResults ?? []).map((kr) => ({
        key: krKey(kr),
        layer: kr.layer,
        area: kr.area,
        subarea: kr.subarea,
        metric: kr.metric,
        label: kr.label,
        unit: kr.unit,
        format: kr.format,
        aggregation: kr.aggregation,
        direction: kr.direction,
        target: targetToField(kr.targetValue, kr.unit),
        tasks: (kr.tasks ?? []).map((t) => ({
          id: t.id,
          name: t.name,
          description: t.description,
          notionUrl: t.notionUrl ?? '',
          monthDue: t.monthDue,
          linkedUserId: t.linkedUserId,
          linkedUser: t.linkedUser,
        })),
      })),
    );
    setFormError(null);
    setApiError(null);
  }, [quarter, okr?.updatedAt, okr?.status]);

  const quarters = useMemo(() => {
    const set = new Set<string>(quarterOptions());
    for (const item of listQuery.data ?? []) set.add(item.quarter);
    set.add(quarter);
    return Array.from(set).sort().reverse();
  }, [listQuery.data, quarter]);

  const availableMetrics = useMemo(() => {
    const selected = new Set(rows.map((r) => r.key));
    return (metricsQuery.data ?? []).filter((m) => !selected.has(m.key));
  }, [metricsQuery.data, rows]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['my-okr', quarter] });
    queryClient.invalidateQueries({ queryKey: ['my-okr-list'] });
  };

  const saveMutation = useMutation({
    mutationFn: () => saveMyOkr({ quarter, keyResults: rowsToInput(rows) }),
    onSuccess: () => {
      setApiError(null);
      invalidate();
    },
    onError: (err: unknown) => setApiError(err instanceof ApiError ? err.message : 'Save failed.'),
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      await saveMyOkr({ quarter, keyResults: rowsToInput(rows) });
      return submitMyOkr(quarter);
    },
    onSuccess: () => {
      setApiError(null);
      invalidate();
    },
    onError: (err: unknown) => setApiError(err instanceof ApiError ? err.message : 'Submit failed.'),
  });

  const taskStatusMutation = useMutation({
    mutationFn: (vars: { taskId: string; status: OkrTaskStatus }) => updateTaskStatus({ quarter, ...vars }),
    onSuccess: () => {
      setApiError(null);
      queryClient.invalidateQueries({ queryKey: ['my-okr', quarter] });
    },
    onError: (err: unknown) => setApiError(err instanceof ApiError ? err.message : 'Could not update status.'),
  });

  const acceptMutation = useMutation({
    mutationFn: () => reviewOkr(okr!.id, { action: 'approve' }),
    onSuccess: () => {
      setApiError(null);
      invalidate();
    },
    onError: (err: unknown) =>
      setApiError(err instanceof ApiError ? err.message : 'Could not accept OKR.'),
  });

  const busy = saveMutation.isPending || submitMutation.isPending;

  const validateForm = (): boolean => {
    if (rows.length === 0) {
      setFormError('Add at least one metric before saving.');
      return false;
    }
    if (rows.some((r) => rowError(r) !== null)) {
      setFormError('Fix the highlighted target values.');
      return false;
    }
    if (rows.some((r) => rowHasTaskError(r))) {
      setFormError('Every task needs a name.');
      return false;
    }
    setFormError(null);
    return true;
  };

  const addMetric = (m: OkrMetric | null) => {
    if (!m) return;
    setRows((prev) => (prev.some((r) => r.key === m.key) ? prev : [...prev, rowFromMetric(m)]));
  };

  const updateTask = (rowKey: string, idx: number, patch: Partial<EditTask>) =>
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey ? { ...r, tasks: r.tasks.map((t, i) => (i === idx ? { ...t, ...patch } : t)) } : r,
      ),
    );

  const addTask = (rowKey: string) =>
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey
          ? {
              ...r,
              tasks: [
                ...r.tasks,
                { name: '', description: '', notionUrl: '', monthDue: 1, linkedUserId: null, linkedUser: null },
              ],
            }
          : r,
      ),
    );

  const removeTask = (rowKey: string, idx: number) =>
    setRows((prev) => prev.map((r) => (r.key === rowKey ? { ...r, tasks: r.tasks.filter((_, i) => i !== idx) } : r)));

  const noManager = user?.managerId == null;
  const canSelfApprove =
    (user?.role === 'admin' || user?.role === 'editor') && okr?.status === 'submitted';

  return (
    <Box>
      <PageHeader
        title="My OKRs"
        subtitle="Commit to quarterly targets on the metrics you track, then submit them to your manager for approval."
        actions={
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="okr-quarter-label">Quarter</InputLabel>
            <Select
              labelId="okr-quarter-label"
              label="Quarter"
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
            >
              {quarters.map((q) => (
                <MenuItem key={q} value={q}>
                  {quarterLabel(q)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      />

      {noManager && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No manager assigned — ask an admin to set your manager so your OKRs can be approved.
        </Alert>
      )}

      {past && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This quarter has ended. You can review it, but new OKRs can only be added to the current or upcoming quarters.
        </Alert>
      )}

      <DataState
        loading={okrQuery.isLoading || metricsQuery.isLoading}
        error={okrQuery.error ?? metricsQuery.error}
        data={okrQuery.data}
      >
        {() => (
          <Stack spacing={3}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 1 }}>
                  <Typography variant="h6">{quarterLabel(quarter)}</Typography>
                  <OkrStatusChip status={okr?.status ?? 'draft'} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {okr ? STATUS_META[okr.status].description : 'No plan yet for this quarter — add metrics below to get started.'}
                </Typography>
                {okr?.status === 'changes_requested' && okr.managerNote && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <strong>Manager note:</strong> {okr.managerNote}
                  </Alert>
                )}
                {okr?.status === 'submitted' && noManager && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Submitted, but you have no manager assigned yet — approval is pending an admin assigning one.
                  </Alert>
                )}
                {canSelfApprove && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckRoundedIcon />}
                      disabled={acceptMutation.isPending}
                      onClick={() => acceptMutation.mutate()}
                    >
                      {acceptMutation.isPending ? 'Accepting…' : 'Accept my OKR'}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      As an {user?.role}, you can approve your own submitted OKR.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {editable && (
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Edit key results
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Pick metrics from your dashboards and set the value you want to reach for the quarter. Each
                    target is judged the way its metric adds up: a quarter total for flows like revenue or orders, a
                    quarter average for rates like open rate or margin, the latest month for levels like cash.
                  </Typography>

                  <Autocomplete
                    options={availableMetrics}
                    getOptionLabel={(o) => metricLabel(o)}
                    groupBy={(o) => `${o.layer} · ${o.area}`}
                    isOptionEqualToValue={(a, b) => a.key === b.key}
                    value={null}
                    blurOnSelect
                    onChange={(_, v) => addMetric(v)}
                    renderInput={(params) => <TextField {...params} label="Add a metric" placeholder="Search metrics…" />}
                    sx={{ mb: 2 }}
                  />

                  {rows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No metrics selected yet.
                    </Typography>
                  ) : (
                    <Stack divider={<Divider flexItem />} spacing={2}>
                      {rows.map((r) => {
                        const err = rowError(r);
                        return (
                          <Box key={r.key}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'center' }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                  {r.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {[r.layer, r.area, AGGREGATION_LABEL[r.aggregation]].filter(Boolean).join(' · ')}
                                </Typography>
                              </Box>
                              <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel>Direction</InputLabel>
                                <Select
                                  label="Direction"
                                  value={r.direction}
                                  onChange={(e) =>
                                    setRows((prev) =>
                                      prev.map((x) => (x.key === r.key ? { ...x, direction: e.target.value as OkrDirection } : x)),
                                    )
                                  }
                                >
                                  <MenuItem value="up">Increase</MenuItem>
                                  <MenuItem value="down">Decrease</MenuItem>
                                </Select>
                              </FormControl>
                              <TextField
                                size="small"
                                label="Target"
                                type="number"
                                value={r.target}
                                error={!!err}
                                helperText={err ?? `${AGGREGATION_LABEL[r.aggregation]} · unit: ${r.unit}`}
                                onChange={(e) =>
                                  setRows((prev) => prev.map((x) => (x.key === r.key ? { ...x, target: e.target.value } : x)))
                                }
                                InputProps={
                                  r.unit === 'percent'
                                    ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                                    : undefined
                                }
                                sx={{ width: 160 }}
                              />
                              <IconButton
                                aria-label="Remove metric"
                                onClick={() => setRows((prev) => prev.filter((x) => x.key !== r.key))}
                              >
                                <DeleteRoundedIcon fontSize="small" />
                              </IconButton>
                            </Stack>

                            <Box sx={{ mt: 1.5, pl: { sm: 1 } }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Contributing tasks — work that will move this metric. (Statuses are tracked below, in any state.)
                              </Typography>
                              <Stack spacing={1.5}>
                                {r.tasks.map((t, i) => (
                                  <Stack
                                    key={i}
                                    direction={{ xs: 'column', sm: 'row' }}
                                    gap={1}
                                    alignItems={{ sm: 'flex-start' }}
                                  >
                                    <TextField
                                      size="small"
                                      label="Task name"
                                      value={t.name}
                                      error={t.name.trim() === ''}
                                      helperText={t.name.trim() === '' ? 'Required' : undefined}
                                      onChange={(e) => updateTask(r.key, i, { name: e.target.value })}
                                      inputProps={{ maxLength: 500 }}
                                      sx={{ flex: 1, minWidth: 160 }}
                                    />
                                    <TextField
                                      size="small"
                                      label="Description"
                                      value={t.description}
                                      multiline
                                      maxRows={3}
                                      onChange={(e) => updateTask(r.key, i, { description: e.target.value })}
                                      sx={{ flex: 1.5, minWidth: 180 }}
                                    />
                                    <TextField
                                      size="small"
                                      label="Notion link"
                                      value={t.notionUrl}
                                      placeholder="https://notion.so/…"
                                      onChange={(e) => updateTask(r.key, i, { notionUrl: e.target.value })}
                                      inputProps={{ maxLength: 2000 }}
                                      sx={{ flex: 1.5, minWidth: 180 }}
                                    />
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                      <InputLabel>Due</InputLabel>
                                      <Select
                                        label="Due"
                                        value={t.monthDue}
                                        onChange={(e) =>
                                          updateTask(r.key, i, { monthDue: Number(e.target.value) as TaskMonth })
                                        }
                                      >
                                        <MenuItem value={1}>Month 1</MenuItem>
                                        <MenuItem value={2}>Month 2</MenuItem>
                                        <MenuItem value={3}>Month 3</MenuItem>
                                      </Select>
                                    </FormControl>
                                    <Autocomplete<DirectoryUser>
                                      size="small"
                                      options={directoryQuery.data ?? []}
                                      getOptionLabel={(o) => o.name}
                                      isOptionEqualToValue={(a, b) => a.id === b.id}
                                      value={(directoryQuery.data ?? []).find((u) => u.id === t.linkedUserId) ?? null}
                                      onChange={(_, v) =>
                                        updateTask(r.key, i, { linkedUserId: v?.id ?? null, linkedUser: v ?? null })
                                      }
                                      renderInput={(params) => (
                                        <TextField {...params} label="Link to teammate" placeholder="Optional" />
                                      )}
                                      sx={{ flex: 1, minWidth: 180 }}
                                    />
                                    <IconButton aria-label="Remove task" onClick={() => removeTask(r.key, i)}>
                                      <DeleteRoundedIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                ))}
                                <Box>
                                  <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => addTask(r.key)}>
                                    Add task
                                  </Button>
                                </Box>
                              </Stack>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}

                  {formError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {formError}
                    </Alert>
                  )}
                  {apiError && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setApiError(null)}>
                      {apiError}
                    </Alert>
                  )}

                  <Stack direction="row" gap={1} sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<SaveRoundedIcon />}
                      disabled={busy}
                      onClick={() => {
                        if (validateForm()) saveMutation.mutate();
                      }}
                    >
                      {saveMutation.isPending ? 'Saving…' : 'Save draft'}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SendRoundedIcon />}
                      disabled={busy}
                      onClick={() => {
                        if (validateForm()) submitMutation.mutate();
                      }}
                    >
                      {submitMutation.isPending ? 'Submitting…' : 'Submit for approval'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {okr && okr.keyResults.length > 0 && (
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Tracking
                  </Typography>
                  <Stack divider={<Divider flexItem />} spacing={2.5}>
                    {okr.keyResults.map((kr) => (
                      <Box key={krKey(kr)}>
                        <KeyResultProgress kr={kr} />
                        {kr.tasks.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <TaskListHeader count={kr.tasks.length} />
                            <Stack divider={<Divider />} sx={{ mt: 0.25 }}>
                              {kr.tasks.map((task) => (
                                <TaskRow
                                  key={task.id}
                                  task={task}
                                  due={taskDueLabel(kr, task)}
                                  linkedUser={task.linkedUser}
                                  control={
                                    <TaskStatusSelect
                                      value={task.status}
                                      disabled={taskStatusMutation.isPending}
                                      onChange={(status) => taskStatusMutation.mutate({ taskId: task.id, status })}
                                    />
                                  }
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {(linkedQuery.data?.length ?? 0) > 0 && (
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Linked to me
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Tasks a teammate linked to you this quarter. Status is owned by the task's author — read-only here.
                  </Typography>
                  <Stack divider={<Divider />}>
                    {(linkedQuery.data ?? []).map((task) => (
                      <Box key={task.taskId} sx={{ py: 1.25 }}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          gap={{ xs: 0.75, sm: 2 }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {task.name}
                              </Typography>
                              {task.owner && (
                                <Stack direction="row" alignItems="center" gap={0.5}>
                                  <Typography variant="caption" color="text.secondary">
                                    from
                                  </Typography>
                                  <LinkedUserChip user={task.owner} />
                                </Stack>
                              )}
                            </Stack>
                            {task.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {task.description}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {task.krLabel}
                            </Typography>
                          </Box>
                          <Stack direction="row" alignItems="center" gap={1.5} sx={{ flexShrink: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {`Month ${task.monthDue}`}
                            </Typography>
                            <TaskStatusChip status={task.status} />
                          </Stack>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        )}
      </DataState>
    </Box>
  );
}
